import { useRef } from 'react';

import { Button, Icon, Stack } from '@eluve/components';

/**
 * Image upload control for a settings row: an outline "Upload" button that
 * opens the file picker, replaced by a bordered preview with a delete
 * button once an image is set.
 */
export const SettingsImageUpload = ({
  src,
  alt,
  accept = 'image/*',
  disabled = false,
  onSelect,
  onDelete,
}: {
  /** URL of the currently uploaded image; falsy renders the upload button. */
  src: string | null | undefined;
  /** Description of the image, e.g. "Signature"; also labels the buttons. */
  alt: string;
  accept?: string;
  disabled?: boolean;
  onSelect: (file: File) => void;
  onDelete: () => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  if (src) {
    return (
      <Stack
        className="rounded-lg border border-border-primary bg-bg-primary p-4"
        gap={4}
        justify="between"
        direction="horizontal"
      >
        <img alt={alt} className="max-h-14 min-w-0" src={src} />
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={onDelete}
          aria-label={`Delete ${alt.toLowerCase()}`}
        >
          <Icon name="Trash2" size="auto" />
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        Upload
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        aria-label={`Upload ${alt.toLowerCase()}`}
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          if (file) {
            onSelect(file);
          }
          e.currentTarget.value = '';
        }}
      />
    </>
  );
};
